// src/components/CapoOfficina/CapoOfficinaRapportini.jsx
import React, { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  query, 
  where,
  orderBy,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase';
import { deleteRapportinoOfficina } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import CapoOfficinaRapportinoForm from './CapoOfficinaRapportinoForm';
import './CapoOfficinaRapportini.css';

const CapoOfficinaRapportini = () => {
  const { currentUser } = useAuth();
  
  // Stati principali
  const [rapportini, setRapportini] = useState([]);
  const [filteredRapportini, setFilteredRapportini] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRapportino, setEditingRapportino] = useState(null);
  const [selectedRapportino, setSelectedRapportino] = useState(null);
  
  // Filtri
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
  
  // Notifiche
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  
  // Stato per gestire l'espansione delle lavorazioni
  const [expandedLavorazioni, setExpandedLavorazioni] = useState({});

  useEffect(() => {
    loadRapportini();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [rapportini, searchTerm, filterMonth, filterYear]);

  const loadRapportini = async () => {
    try {
      setIsLoading(true);
      
      const rapportiniQuery = query(
        collection(db, 'rapportiniOfficina'),
        where('stato', '==', 'Attivo'),
        orderBy('data', 'desc')
      );
      
      const snapshot = await getDocs(rapportiniQuery);
      const data = snapshot.docs.map(doc => {
        const rapportino = doc.data();
        return {
          id: doc.id,
          ...rapportino,
          data: rapportino.data?.toDate ? rapportino.data.toDate() : new Date(rapportino.data)
        };
      });
      
      setRapportini(data);
      console.log(`Caricati ${data.length} rapportini officina`);
    } catch (error) {
      console.error('Errore nel caricamento rapportini:', error);
      showNotification('Errore nel caricamento rapportini', 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  const applyFilters = () => {
    let filtered = [...rapportini];

    // Filtro per ricerca testuale
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(rap => 
        (rap.nomeRapportino && rap.nomeRapportino.toLowerCase().includes(search)) ||
        (rap.noteVarie && rap.noteVarie.toLowerCase().includes(search))
      );
    }

    // Filtro per mese
    if (filterMonth) {
      filtered = filtered.filter(rap => {
        const rapMonth = (rap.data.getMonth() + 1).toString();
        return rapMonth === filterMonth;
      });
    }

    // Filtro per anno
    if (filterYear) {
      filtered = filtered.filter(rap => {
        const rapYear = rap.data.getFullYear().toString();
        return rapYear === filterYear;
      });
    }

    setFilteredRapportini(filtered);
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 4000);
  };

  const handleNewRapportino = () => {
    setEditingRapportino(null);
    setSelectedRapportino(null);
    setShowForm(true);
  };

  const handleEditRapportino = (rapportino) => {
    setEditingRapportino(rapportino);
    setSelectedRapportino(rapportino);
    setShowForm(true);
  };

  const handleViewRapportino = (rapportino) => {
    setSelectedRapportino(rapportino);
    setExpandedLavorazioni({}); // Reset espansioni quando si apre un nuovo rapportino
    setShowForm(false);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingRapportino(null);
    setSelectedRapportino(null);
    loadRapportini();
  };

  const handleDeleteRapportino = async (rapportino, event) => {
    event.stopPropagation();
    
    const conferma = window.confirm(
      `Sei sicuro di voler eliminare il rapportino del ${rapportino.data.toLocaleDateString('it-IT')}?\n\n` +
      `Lavorazioni: ${rapportino.lavorazioni?.length || 0} interventi\n` +
      `Personale: ${rapportino.personalePresente?.length || 0} persone\n\n` +
      'Questa azione non può essere annullata.'
    );
    
    if (!conferma) return;
    
    try {
      showNotification('Eliminazione in corso...', 'info');
      await deleteRapportinoOfficina(rapportino.id);
      await loadRapportini();
      showNotification('✅ Rapportino eliminato con successo', 'success');
    } catch (error) {
      console.error('Errore nell\'eliminazione:', error);
      showNotification('❌ Errore nell\'eliminazione del rapportino', 'error');
    }
  };

  // Funzione per gestire l'espansione/chiusura delle lavorazioni
  const toggleLavorazione = (index) => {
    setExpandedLavorazioni(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear; i >= currentYear - 5; i--) {
      years.push(i);
    }
    return years;
  };

  if (showForm) {
    return (
      <CapoOfficinaRapportinoForm
        rapportino={editingRapportino}
        onBack={handleCloseForm}
        onSave={handleCloseForm}
      />
    );
  }

  if (selectedRapportino && !showForm) {
    return (
      <div className="rapportino-detail-container">
        <div className="rapportino-detail-header">
          <button onClick={() => setSelectedRapportino(null)} className="btn-back">
            Indietro
          </button>
          <h2>Dettaglio Rapportino</h2>
          <button onClick={() => handleEditRapportino(selectedRapportino)} className="btn-edit">
            Modifica
          </button>
        </div>

        <div className="rapportino-detail-content">
          <div className="detail-section">
            <h3>📅 Informazioni Generali</h3>
            <p><strong>Data:</strong> {selectedRapportino.data.toLocaleDateString('it-IT')}</p>
            <p><strong>Redatto da:</strong> {selectedRapportino.redattoDaNome || 'N/D'}</p>
          </div>

          <div className="detail-section">
            <h3>👥 Personale Presente</h3>
            {selectedRapportino.personalePresente && selectedRapportino.personalePresente.length > 0 ? (
              <ul>
                {selectedRapportino.personalePresente.map((persona, idx) => (
                  <li key={idx}>
                    {persona.nome} {persona.cognome} - {persona.incarico}
                  </li>
                ))}
              </ul>
            ) : (
              <p>Nessun personale registrato</p>
            )}
          </div>

          <div className="detail-section">
            <h3>🔧 Lavorazioni Effettuate</h3>
            
            {/* Progress Bar Status */}
            {selectedRapportino.lavorazioni && selectedRapportino.lavorazioni.length > 0 && (() => {
              const totale = selectedRapportino.lavorazioni.length;
              const concluse = selectedRapportino.lavorazioni.filter(l => l.status === 'Lavorazione conclusa').length;
              const percentuale = Math.round((concluse / totale) * 100);
              
              return (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span>
                      <strong>Progresso Lavorazioni:</strong>
                    </span>
                    <span>
                      <span className="stat-concluse" style={{ marginRight: '8px' }}>
                        ✅ {concluse} completate
                      </span>
                      <span className="stat-in-corso">
                        🔧 {totale - concluse} in corso
                      </span>
                    </span>
                  </div>
                  
                  <div className="status-progress">
                    <div 
                      className="status-progress-bar"
                      style={{ width: `${percentuale}%` }}
                      title={`${percentuale}% completato`}
                    ></div>
                  </div>
                  
                  <div style={{ textAlign: 'right', fontSize: '0.85rem', color: '#6c757d', marginTop: '4px' }}>
                    {percentuale}% completato
                  </div>
                </div>
              );
            })()}
            
            {/* Lista Lavorazioni Espandibili */}
            {selectedRapportino.lavorazioni && selectedRapportino.lavorazioni.length > 0 ? (
              <div className="lavorazioni-list">
                {selectedRapportino.lavorazioni.map((lavorazione, idx) => (
                  <div key={idx} className="lavorazione-item">
                    {/* Header Lavorazione - Cliccabile */}
                    <div 
                      className="lavorazione-header"
                      onClick={() => toggleLavorazione(idx)}
                    >
                      <div className="lavorazione-header-left">
                        <span className="lavorazione-icon">
                          {lavorazione.status === 'Lavorazione conclusa' ? '✅' : '🔧'}
                        </span>
                        <div className="lavorazione-title-info">
                          <h4 className="lavorazione-title">
                            {lavorazione.targaMezzo || 'N/D'} - {lavorazione.tipoLavorazione || 'Lavorazione'}
                          </h4>
                          <span className={`lavorazione-status ${lavorazione.status === 'Lavorazione conclusa' ? 'conclusa' : 'in-corso'}`}>
                            {lavorazione.status || 'Lavorazione in corso'}
                          </span>
                        </div>
                      </div>
                      <div className="lavorazione-header-right">
                        <span className="expand-icon">
                          {expandedLavorazioni[idx] ? '▼' : '▶'}
                        </span>
                      </div>
                    </div>

                    {/* Dettagli Lavorazione - Espandibile */}
                    {expandedLavorazioni[idx] && (
                      <div className="lavorazione-details">
                        
                        {/* Mezzo */}
                        {lavorazione.nomeMezzo && (
                          <div className="detail-row">
                            <strong className="detail-label">🚗 Mezzo:</strong>
                            <div className="detail-value">
                              <span className="badge badge-mezzo">
                                {lavorazione.targaMezzo} - {lavorazione.nomeMezzo}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Tipo Lavorazione */}
                        {lavorazione.tipoLavorazione && (
                          <div className="detail-row">
                            <strong className="detail-label">🔨 Tipo Lavorazione:</strong>
                            <div className="detail-value">
                              <span className="badge badge-tipo">
                                {lavorazione.tipoLavorazione}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Ore Investite */}
                        {lavorazione.oreInvestite !== undefined && lavorazione.oreInvestite !== null && (
                          <div className="detail-row">
                            <strong className="detail-label">⏱️ Ore Investite:</strong>
                            <div className="detail-value">
                              <span className="badge badge-ore">
                                {lavorazione.oreInvestite} {lavorazione.oreInvestite === 1 ? 'ora' : 'ore'}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Stato Lavorazione */}
                        {lavorazione.status && (
                          <div className="detail-row">
                            <strong className="detail-label">📊 Stato:</strong>
                            <div className="detail-value">
                              <span className={`badge ${lavorazione.status === 'Lavorazione conclusa' ? 'badge-status-conclusa' : 'badge-status-in-corso'}`}>
                                {lavorazione.status}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Km Mezzo */}
                        {lavorazione.kmMezzo && (
                          <div className="detail-row">
                            <strong className="detail-label">🛣️ Chilometraggio:</strong>
                            <div className="detail-value">
                              <span className="badge badge-km">
                                {lavorazione.kmMezzo} km
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Personale Assegnato */}
                        {lavorazione.personaleAssegnato && lavorazione.personaleAssegnato.length > 0 && (
                          <div className="detail-row">
                            <strong className="detail-label">👥 Personale Assegnato:</strong>
                            <div className="detail-value">
                              <div className="personale-list">
                                {lavorazione.personaleAssegnato.map((persona, pidx) => (
                                  <div key={pidx} className="personale-item">
                                    <span className="personale-icon">👤</span>
                                    <div className="personale-info">
                                      <span className="personale-name">
                                        {persona.nome} {persona.cognome}
                                      </span>
                                      <span className="personale-role">
                                        {persona.incarico}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Note */}
                        {lavorazione.note && lavorazione.note.trim() !== '' && (
                          <div className="detail-row">
                            <strong className="detail-label">💬 Note:</strong>
                            <div className="detail-value detail-text note-text">
                              {lavorazione.note}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p>Nessuna lavorazione registrata</p>
            )}
          </div>

          {selectedRapportino.noteVarie && (
            <div className="detail-section">
              <h3>📝 Note</h3>
              <p>{selectedRapportino.noteVarie}</p>
            </div>
          )}
        </div>

        <div className="rapportino-detail-footer">
          <button 
            onClick={(e) => handleDeleteRapportino(selectedRapportino, e)} 
            className="btn-delete"
          >
            Elimina Rapportino
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rapportini-container">
      <div className="rapportini-header">
        <h2>📋 Rapportini Officina</h2>
        <button onClick={handleNewRapportino} className="btn-new-rapportino">
          Nuovo Rapportino
        </button>
      </div>

      {notification.show && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      {/* Filtri */}
      <div className="rapportini-filters">
        <div className="filter-group">
          <label>🔍 Cerca</label>
          <input
            type="text"
            placeholder="Cerca rapportino..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="filter-input"
          />
        </div>

        <div className="filter-group">
          <label>📅 Mese</label>
          <select
            value={filterMonth}
            onChange={(e) => setFilterMonth(e.target.value)}
            className="filter-select"
          >
            <option value="">Tutti</option>
            <option value="1">Gennaio</option>
            <option value="2">Febbraio</option>
            <option value="3">Marzo</option>
            <option value="4">Aprile</option>
            <option value="5">Maggio</option>
            <option value="6">Giugno</option>
            <option value="7">Luglio</option>
            <option value="8">Agosto</option>
            <option value="9">Settembre</option>
            <option value="10">Ottobre</option>
            <option value="11">Novembre</option>
            <option value="12">Dicembre</option>
          </select>
        </div>

        <div className="filter-group">
          <label>📆 Anno</label>
          <select
            value={filterYear}
            onChange={(e) => setFilterYear(e.target.value)}
            className="filter-select"
          >
            {generateYearOptions().map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Lista rapportini */}
      {isLoading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Caricamento rapportini...</p>
        </div>
      ) : filteredRapportini.length === 0 ? (
        <div className="empty-state">
          <p>📋 Nessun rapportino trovato</p>
          <button onClick={handleNewRapportino} className="btn-create-first">
            Crea il primo rapportino
          </button>
        </div>
      ) : (
        <div className="rapportini-grid">
          {filteredRapportini.map(rapportino => (
            <div key={rapportino.id} className="rapportino-card">
              <div className="rapportino-card-header">
                <h3>📅 {rapportino.data.toLocaleDateString('it-IT')}</h3>
              </div>

              <div className="rapportino-card-body">
                <div className="rapportino-info">
                  <span className="info-label">👥 Personale:</span>
                  <span className="info-value">
                    {rapportino.personalePresente?.length || 0} persone
                  </span>
                </div>

                <div className="rapportino-info">
                  <span className="info-label">🔧 Lavorazioni:</span>
                  <span className="info-value">
                    {rapportino.lavorazioni?.length || 0} interventi
                    {rapportino.lavorazioni?.length > 0 && (
                      <span className="lavorazioni-stats">
                        {(() => {
                          const inCorso = rapportino.lavorazioni.filter(l => l.status === 'Lavorazione in corso' || !l.status).length;
                          const concluse = rapportino.lavorazioni.filter(l => l.status === 'Lavorazione conclusa').length;
                          return (
                            <>
                              {inCorso > 0 && (
                                <span className="stat-in-corso" title="Lavorazioni in corso">
                                  🔧 {inCorso}
                                </span>
                              )}
                              {concluse > 0 && (
                                <span className="stat-concluse" title="Lavorazioni concluse">
                                  ✅ {concluse}
                                </span>
                              )}
                            </>
                          );
                        })()}
                      </span>
                    )}
                  </span>
                </div>

                <div className="rapportino-info">
                  <span className="info-label">✏️ Redatto da:</span>
                  <span className="info-value">
                    {rapportino.redattoDaNome || 'N/D'}
                  </span>
                </div>
              </div>

              <div className="rapportino-card-actions">
                <button
                  onClick={() => handleViewRapportino(rapportino)}
                  className="btn-view"
                  title="Visualizza rapportino"
                >
                </button>
                <button
                  onClick={() => handleEditRapportino(rapportino)}
                  className="btn-edit"
                  title="Modifica rapportino"
                >
                </button>
                <button
                  onClick={(e) => handleDeleteRapportino(rapportino, e)}
                  className="btn-delete"
                  title="Elimina rapportino"
                >
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CapoOfficinaRapportini;